import React from 'react';
import { Bone, TextBone } from '../CampaignSkeleton/CampaignSkeleton';
import type { CampaignObjectiveId } from '../../types';
import {
  FunnelSkeleton,
  KpiSkeleton,
  LeakSkeleton,
  ProgressSkeleton,
  RunwaySkeleton,
} from '../CampaignPerformance/PerformanceSkeleton';

/*
 * The campaign detail while it loads (VOX-317). It loads in two steps — the
 * listing says which campaign and in what status, then Voice's read brings its
 * configuration — so the skeleton draws as much as is known at each one: with
 * the status known the mode is too (operation or configuration), and the body
 * takes that mode's shape.
 */

const CONTROL = 'var(--silia-ctl-height)';

/**
 * The header's right side: the sections' switch (when there is one) and the
 * action buttons, at the controls' height.
 */
export const DetailActionsSkeleton: React.FC<{ segmented?: boolean; buttons?: number }> = ({
  segmented = true,
  buttons = 2,
}) => (
  <>
    {segmented && <Bone height={CONTROL} className="campaign-detail__segmented-bone" />}
    {Array.from({ length: buttons }, (_, i) => (
      <Bone key={i} width={104} height={CONTROL} />
    ))}
  </>
);

/** Performance's filters: audience, subagent and the period at the far right. */
const FiltersSkeleton: React.FC = () => (
  <div className="campaign-performance__filters">
    <span className="campaign-performance__filter--audience">
      <Bone height={CONTROL} />
    </span>
    <span className="campaign-performance__filter--subagent">
      <Bone height={CONTROL} />
    </span>
    <span className="campaign-performance__filter--period">
      <Bone height={CONTROL} />
    </span>
  </div>
);

/**
 * Performance before the configuration arrives. With the objective (known from
 * the listing) the KPI row and the funnel take its exact shape; without it, a
 * row of four cards stands in.
 */
export const PerformanceBodySkeleton: React.FC<{ objective?: CampaignObjectiveId }> = ({
  objective,
}) => (
  <div className="campaign-performance">
    <FiltersSkeleton />
    <ProgressSkeleton />
    {objective ? (
      <>
        <KpiSkeleton objective={objective} />
        <FunnelSkeleton objective={objective} />
        <LeakSkeleton />
        <RunwaySkeleton />
      </>
    ) : (
      <div className="campaign-performance__kpis">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="campaign-performance__stat">
            <TextBone width="60%" size={12} lineHeight={20} />
            <TextBone width="45%" size={24} lineHeight={30} />
          </div>
        ))}
      </div>
    )}
  </div>
);

/** One numbered section of the form (`WizardSection`): badge, title, hint, fields. */
const SectionSkeleton: React.FC<{ fields: number }> = ({ fields }) => (
  <div className="wizard-section">
    <div className="wizard-section__heading">
      <Bone width={24} height={24} shape="circle" className="campaign-detail__badge-bone" />
      <div className="wizard-section__text campaign-detail__section-text">
        <TextBone width="40%" size={16} lineHeight={24} />
        <TextBone width="75%" size={12} lineHeight={20} />
      </div>
    </div>
    {Array.from({ length: fields }, (_, i) => (
      <div key={i} className="campaign-skeleton__stack">
        <TextBone width={120} size={12} lineHeight={20} />
        <Bone height={CONTROL} />
      </div>
    ))}
  </div>
);

/**
 * A campaign that never ran IS its configuration, edited in place: the wizard's
 * two columns — the form with its sections, and the summary aside.
 */
export const ConfigurationBodySkeleton: React.FC = () => (
  <div className="campaign-wizard__split">
    <div className="campaign-wizard__form">
      <SectionSkeleton fields={2} />
      <div className="campaign-wizard__step-divider" />
      <SectionSkeleton fields={1} />
    </div>
    <div className="campaign-wizard__column-divider" />
    <div className="campaign-wizard__aside">
      <div className="campaign-summary">
        <div className="campaign-summary__block">
          <TextBone width="45%" size={12} lineHeight={20} />
          <TextBone width="55%" size={30} lineHeight={36} />
          <TextBone width="80%" size={12} lineHeight={20} />
        </div>
        <div className="campaign-summary__split" />
        <div className="campaign-summary__block">
          <TextBone width="40%" size={12} lineHeight={20} />
          <TextBone width="70%" size={12} lineHeight={20} />
          <TextBone width="60%" size={12} lineHeight={20} />
        </div>
      </div>
    </div>
  </div>
);
